import React, { useEffect, useState } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

const TPTYPE_LABEL = { T: '학과', P: '실기' };

// 학생평가결과 조회: 연도/반구분/학년/반/과목/실기·학과로 coursexam 자료를 조회한다 (과목, 실기/학과는 "전체" 가능).
// [입력] 버튼은 관리자 권한(admin/staff)만 활성화되며, 누르면 평가결과입력 페이지로 이동한다.
// mobile: 스마트폰에서는 번호/학년/반/비고 열을 숨긴다.
const THIS_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => THIS_YEAR - i);

export default function ExamResults({ mobile = false, onNavigate }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'staff';

  const [year, setYear] = useState(THIS_YEAR);
  const [classtype, setClasstype] = useState('M');
  const [grade, setGrade] = useState(1);
  const [classOptions, setClassOptions] = useState([]);
  const [classNo, setClassNo] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [amtcode, setAmtcode] = useState(''); // '' = 전체
  const [tptype, setTptype] = useState(''); // '' = 전체

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    api.get('/exam-subjects').then((res) => setSubjects(res.data));
  }, []);

  useEffect(() => {
    let cancelled = false;
    api.get('/classes', { params: { grade, classtype } }).then((res) => {
      if (cancelled) return;
      setClassOptions(res.data);
      setClassNo(res.data.length ? String(res.data[0]) : '');
    });
    return () => {
      cancelled = true;
    };
  }, [grade, classtype]);

  useEffect(() => {
    if (!classNo) {
      setRows([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setMessage('');
    api
      .get('/exam-results', {
        params: {
          year,
          classtype,
          grade,
          class_no: Number(classNo),
          ...(amtcode ? { amtcode } : {}),
          ...(tptype ? { tptype } : {}),
        },
      })
      .then((res) => {
        if (!cancelled) setRows(res.data);
      })
      .catch((err) => {
        if (!cancelled) setMessage(err.response?.data?.detail || '평가결과를 불러오지 못했습니다.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [year, classtype, grade, classNo, amtcode, tptype]);

  const hideCls = mobile ? 'exam-col-hide' : '';

  return (
    <div className={`page exam-scores-page exam-results-page${mobile ? ' exam-scores-mobile' : ''}`}>
      <div className="page-header">
        <h2>항공정비사과정 평가결과</h2>
      </div>

      <div className="card">
        <div className="form-row exam-filter-row exam-results-filter">
          <div className="form-field">
            <label>연도</label>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {YEAR_OPTIONS.map((y) => (
                <option key={y} value={y}>{y}년</option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label>반구분</label>
            <select value={classtype} onChange={(e) => setClasstype(e.target.value)}>
              <option value="R">정규반</option>
              <option value="M">면장반</option>
            </select>
          </div>
          <div className="form-field">
            <label>학년</label>
            <select value={grade} onChange={(e) => setGrade(Number(e.target.value))}>
              <option value={1}>1학년</option>
              <option value={2}>2학년</option>
              <option value={3}>3학년</option>
            </select>
          </div>
          <div className="form-field">
            <label>반</label>
            <select value={classNo} onChange={(e) => setClassNo(e.target.value)}>
              {classOptions.length === 0 && <option value="">-</option>}
              {classOptions.map((c) => (
                <option key={c} value={c}>{c}반</option>
              ))}
            </select>
          </div>
          <div className="form-field exam-field-subject">
            <label>과목</label>
            <select value={amtcode} onChange={(e) => setAmtcode(e.target.value)}>
              <option value="">전체</option>
              {subjects.map((s) => (
                <option key={s.amtcode} value={s.amtcode}>{s.subject}</option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label>실기/학과</label>
            <select value={tptype} onChange={(e) => setTptype(e.target.value)}>
              <option value="">전체</option>
              <option value="T">학과</option>
              <option value="P">실기</option>
            </select>
          </div>
          <div className="form-field exam-field-actions">
            <label>&nbsp;</label>
            <div className="exam-actions">
              <button
                type="button"
                className="btn-primary exam-btn"
                disabled={!isAdmin}
                title={isAdmin ? '평가결과를 입력합니다' : '관리자 권한이 필요합니다'}
                onClick={() => onNavigate && onNavigate('exam-scores')}
              >
                입력
              </button>
            </div>
          </div>
        </div>

        {message && <div className="form-message">{message}</div>}

        <div className="table-scroll">
          <table className="data-table exam-scores-table exam-results-table">
            <thead>
              <tr>
                <th className={`exam-col-no ${hideCls}`}>번호</th>
                <th className={`exam-col-sm ${hideCls}`}>학년</th>
                <th className={`exam-col-sm ${hideCls}`}>반</th>
                <th className="exam-col-name">이름</th>
                <th>과목</th>
                <th className="exam-col-tp">실기/학과</th>
                <th className="exam-col-date">평가일자</th>
                <th className="exam-col-score">점수</th>
                <th className={hideCls}>비고</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.exmid}>
                  <td className={hideCls}>{r.studno ?? i + 1}</td>
                  <td className={hideCls}>{r.grade}</td>
                  <td className={hideCls}>{r.class_no}</td>
                  <td>{r.name}</td>
                  <td>{r.subject}</td>
                  <td>{TPTYPE_LABEL[r.tptype] || ''}</td>
                  <td>{r.exmdate}</td>
                  <td>{r.score}</td>
                  <td className={`exam-result-remark ${hideCls}`}>{r.remark}</td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={9} className="empty-row">조회된 평가결과가 없습니다.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
